import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useUrlTab } from "../hooks/useUrlTab";
import { AcctShell } from "../features/acct/AcctShell";
import { BillsStats } from "../features/acct/BillsStats";
import { BillsToolbar } from "../features/acct/BillsToolbar";
import { BillsFilters } from "../features/acct/BillsFilters";
import { BillsTable } from "../features/acct/BillsTable";
import { BillFormV2 } from "../features/acct/BillFormV2";
import { BillsApprovalsView } from "../features/acct/BillsApprovalsView";
import { useBills } from "../features/acct/useBills";
import { useAcctLookups } from "../features/acct/useAcctLookups";
import { localName } from "../lib/localName";

const STAT_TO_STATUS = { overdue: "unpaid", paid: "paid", partial: "partial", pending: "pending_approval" };

// صفحة الفواتير — الشريط العلوي (BillsToolbar) يبقى مثبتاً في كل الأنماط ويُبرز التبويب النشط.
export default function AcctBillsPage() {
  const { t } = useTranslation();
  const lookups = useAcctLookups();
  const bills = useBills();
  const [view, setView] = useUrlTab("list");
  const [activeStat, setActiveStat] = useState(null);

  const branchesById  = useMemo(() => Object.fromEntries((lookups.branches || []).map((b) => [b.id, localName(b)])), [lookups.branches]);
  const suppliersById = useMemo(() => Object.fromEntries((lookups.suppliers || []).map((s) => [s.id, localName(s)])), [lookups.suppliers]);
  const accountsByCode = useMemo(() => Object.fromEntries((lookups.accounts || []).map((a) => [a.code, localName(a)])), [lookups.accounts]);
  const categories = useMemo(() =>
    (lookups.accounts || []).filter((a) => (a.code || "").startsWith("6") && a.is_leaf), [lookups.accounts]);

  const pickStat = (key) => {
    setActiveStat((prev) => (prev === key ? null : key));
    bills.setStatus(activeStat === key ? "all" : STAT_TO_STATUS[key]);
  };

  return (
    <AcctShell>
      <BillsToolbar active={view}
                    onNew={() => setView("add")}
                    onList={() => setView("list")}
                    onApprovals={() => setView("approvals")}
                    onReports={() => toast(t("acct.bills.soon"))}
                    onAlerts={() => toast(t("acct.bills.soon"))}
                    alerts={bills.stats.overdue}
                    approvals={bills.stats.pending} />
      {view === "add" ? (
        <BillFormV2 accounts={lookups.accounts} boxes={lookups.boxes}
                    suppliers={lookups.suppliers} branches={lookups.branches}
                    onCancel={() => setView("list")}
                    onSaved={() => { setView("list"); bills.load(); lookups.reload(); }} />
      ) : view === "approvals" ? (
        <BillsApprovalsView bills={bills.all} boxes={lookups.boxes} employees={lookups.employees}
                            branchesById={branchesById} suppliersById={suppliersById}
                            accountsByCode={accountsByCode}
                            onReload={() => bills.load()} />
      ) : (
        <>
          <BillsStats stats={bills.stats} active={activeStat} onSelect={pickStat} />
          <BillsFilters status={bills.status} setStatus={bills.setStatus}
                        branchId={bills.branchId} setBranchId={bills.setBranchId}
                        category={bills.category} setCategory={bills.setCategory}
                        branches={lookups.branches || []} categories={categories}
                        onExportExcel={() => toast(t("acct.bills.soon"))}
                        onExportPdf={() => toast(t("acct.bills.soon"))} />
          <BillsTable rows={bills.rows}
                      branchesById={branchesById}
                      suppliersById={suppliersById}
                      accountsByCode={accountsByCode} />
        </>
      )}
    </AcctShell>
  );
}
